import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpService } from '../core/http.service';
import { WebviewService } from '../platform/webview.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { Language } from './settings.service';

// Loose alias used at the fetch/seed seams. The public `languageJSON` field stays
// permissive so the app can keep reading keys with dot access (`languageJSON.Some_Key`)
// under `noPropertyAccessFromIndexSignature`.
export type LanguageJSON = Record<string, string>;

/** App codes that are not the BCP 47 tag of their language (`se` would be Northern Sami). */
const HTML_LANG: Record<string, string> = { se: 'sv', pt: 'pt-PT', br: 'pt-BR', sr: 'sr-Cyrl', zh: 'zh-Hans' };

/**
 * SN Pro has no CJK or Devanagari glyphs; these stylesheets add faces to the 'SN Pro' family
 * (see scripts/generate-fallback-fonts.js). Loaded only for their language: they are large,
 * and Chinese and Japanese share code points but not glyph shapes.
 */
const SCRIPT_FONT_CSS: Record<string, string> = {
    zh: 'assets/fonts/noto-sans-sc/noto-sans-sc.css',
    ja: 'assets/fonts/noto-sans-jp/noto-sans-jp.css',
    hi: 'assets/fonts/mukta/mukta.css',
};

/** Languages with localized store badges under assets/icons/default/social/. */
const STORE_BADGE_LANGUAGES = new Set(['de', 'en', 'es', 'fr', 'it', 'nl', 'no', 'se']);

@Injectable({
    providedIn: 'root'
})
export class LanguageService {

    /**
     * Backed by a signal so that templates reading languageService.languageJSON.Some_Key
     * become consumers of it: loading a new language re-renders them with no manual
     * change detection. The property shape is unchanged for callers.
     */
    private readonly _languageJSON = signal<any>({});

    public get languageJSON(): any {
        return this._languageJSON();
    }
    public set languageJSON(json: any) {
        this._languageJSON.set(json);
    }

    /** Emits the currently loaded language code (null before the first load). */
    public readonly activeLanguage$ = new BehaviorSubject<string | null>(null);

    /** Signal-backed: templates read it through getLanguage(). */
    private readonly currentCode = signal<string>('');
    private readonly requested$ = new Subject<string>();

    private readonly httpService = inject(HttpService);
    private readonly webViewService = inject(WebviewService);
    private readonly consoleLoggerService = inject(ConsoleLoggerService);
    private readonly document = inject(DOCUMENT);

    constructor() {
        // setLanguage already skips repeats of the current code; no distinctUntilChanged here, so a failed code can be retried.
        this.requested$.pipe(
            switchMap((code) => this.httpService.getLanguageJSON(code).pipe(
                map((json) => ({ code, json: json as LanguageJSON })),
                catchError((error) => {
                    this.consoleLoggerService.error('Failed to load language JSON', error);
                    this.revertToApplied(code);
                    return EMPTY; // keep the last successfully loaded language
                })
            )),
            takeUntilDestroyed()
        ).subscribe(({ code, json }) => this.apply(code, json));
    }

    /** Runtime language switch: persists, notifies the webview and fetches the new JSON. */
    public setLanguage(code: string): void {
        if (code === this.currentCode()) return;
        this.currentCode.set(code);
        localStorage.setItem('language', code);
        this.webViewService.SetUserLanguage(code);
        this.requested$.next(code);
    }

    /** Startup seed from the resolver's already-fetched JSON (no refetch). */
    public initLanguage(code: string, json: LanguageJSON): void {
        this.currentCode.set(code);
        this.apply(code, json);
    }

    public getLanguage(): string {
        return this.currentCode();
    }

    public getLanguageList(): { code: string, name: string }[] {
        return Language;
    }

    /** Language folder of the App Store / Google Play badges; English where none is localized. */
    public getStoreBadgeLanguage(): string {
        const code = this.currentCode();
        return STORE_BADGE_LANGUAGES.has(code) ? code : 'en';
    }

    /** After a failed fetch, report the language that is actually shown again (code, storage, webview). */
    private revertToApplied(failedCode: string): void {
        const applied = this.activeLanguage$.value;
        if (!applied || this.currentCode() !== failedCode) return;
        this.currentCode.set(applied);
        localStorage.setItem('language', applied);
        this.webViewService.SetUserLanguage(applied);
    }

    private apply(code: string, json: LanguageJSON): void {
        this.languageJSON = json;
        this.document.documentElement.lang = HTML_LANG[code] ?? code;
        this.loadScriptFont(code);
        this.activeLanguage$.next(code);
    }

    private loadScriptFont(code: string): void {
        const id = 'script-font';
        const href = SCRIPT_FONT_CSS[code];
        let link = this.document.getElementById(id) as HTMLLinkElement | null;
        if (!href) {
            link?.remove();
            return;
        }
        if (!link) {
            link = this.document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            // Appended after the bundled styles: for overlapping unicode-ranges the last face defined wins.
            this.document.head.appendChild(link);
        }
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    }

    public onlyEnglish = {
        "SUBS_ERR_001": "The subscription id field is required.",
        "SUBS_ERR_002": "The selected old vehicle id is required.",
        "SUBS_ERR_003": "The selected old vehicle id is invalid.",
        "SUBS_ERR_004": "The selected new vehicle id is required.",
        "SUBS_ERR_005": "The selected new vehicle id is invalid.",
        "SUBS_ERR_006": "The selected vehicle id is required.",
        "SUBS_ERR_007": "The selected user vehicle id is required."
    }
}
